/**
 * TRENDS — every analytics panel, on one filter.
 *
 * This is `/dashboard/analytics` in full: pulse, building league, composition,
 * severity, portfolio roll-up, hour of day, who recorded it, dwell, quality,
 * turnover, rhythm and the all-figures table.
 *
 * Every panel reads its own slice and renders its own skeleton, error and retry.
 * There is no global spinner: panels arrive independently, and one panel's 500
 * never blanks its siblings.
 */
import React, { useCallback, useMemo } from 'react';
import {
  AnalyticsBatchResult,
  AnalyticsMeta,
} from '../../../../../api/analytics-client';
import { DashboardData } from '../data/dashboard-data';
import { PANEL } from '../data/panel-plan';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { ChartUnavailable } from '../charts/chart-empty';
import { Panel, PanelHead, PanelTitle, PanelTitleGroup, PanelBody } from '../charts/chart.styled';
import { useChartDrill } from '../charts/use-chart-drill';
import { useChartZoom } from '../charts/use-chart-zoom';
import { PanelGrid } from '../dashboard-screen.styled';
import { PulsePanel } from '../panels/pulse-panel';
import { mergeCompareScope } from '../panels/panel-utils';
import { BarPanel } from '../panels/bar-panel';
import { RatePanel } from '../panels/rate-panel';
import { ScatterPanel } from '../panels/scatter-panel';
import { TreemapPanel } from '../panels/treemap-panel';
import { SmallMultiplesPanel } from '../panels/small-multiples-panel';
import { CompositionPanel } from '../panels/composition-panel';
import { SeverityPanel } from '../panels/severity-panel';
import { HourPanel } from '../panels/hour-panel';
import { DwellPanel } from '../panels/dwell-panel';
import { QualityPanel } from '../panels/quality-panel';
import { TurnoverPanel } from '../panels/turnover-panel';
import { RhythmPanel } from '../panels/rhythm-panel';
import { AllFiguresPanel } from '../panels/all-figures-panel';
import { format, partialDayEndsAt } from '../charts/chart-theme';

export interface TabContext {
  filter: DashboardFilter;
  filterMeta?: FilterMeta;
  meta?: AnalyticsMeta;
  data: DashboardData;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  isMobile: boolean;
  onExportPanelPng: (panelId: string, svg: SVGSVGElement | null) => void;
  onExportPanelCsv: (panelId: string) => void;
}

/** Batch results keyed by the panel id that asked for them. */
/**
 * PANEL ID -> RESULT, for every panel on the screen.
 *
 * WITH COMPARISON MODE ON, the second scope's answer is folded into the same
 * result as `compareValue` — the slot every panel already draws as a muted
 * second series. See `mergeCompareScope`. The panels need no comparison code of
 * their own, and the two comparisons cannot collide because the reducer makes
 * them mutually exclusive.
 */
export const useResults = (
  data: DashboardData
): Record<string, AnalyticsBatchResult | undefined> =>
  useMemo(() => {
    const map: Record<string, AnalyticsBatchResult | undefined> = {};
    for (const result of data.batch.data?.results || []) map[result.id] = result;

    const other = data.batchB.data?.results;
    if (!other) return map;

    const byId = new Map(other.map(result => [result.id, result]));
    for (const id of Object.keys(map)) {
      map[id] = mergeCompareScope(map[id], byId.get(id));
    }

    return map;
  }, [data.batch.data, data.batchB.data]);

/**
 * A PANEL THIS MEASURE CANNOT HONESTLY FILL.
 *
 * "Parcels on the shelf per hour of the day" is not a question, and the server
 * would answer it by silently regrouping to `building` and saying so in a note.
 * That is the right server behaviour and the wrong screen: a panel captioned
 * "Hour of day" showing a building split is a chart with the wrong title, which
 * is worse than an empty one. So the panel is NOT REQUESTED, and it says which
 * measure it would need.
 */
const Unavailable: React.FC<{
  title: string;
  measureLabel: string;
  dimension: string;
  wide?: boolean;
}> = ({ title, measureLabel, dimension, wide }) => (
  <Panel wide={wide} data-testid={`unavailable-${title}`}>
    <PanelHead>
      <PanelTitleGroup>
        <PanelTitle>{title}</PanelTitle>
      </PanelTitleGroup>
    </PanelHead>
    <PanelBody>
      <ChartUnavailable
        measureLabel={measureLabel}
        dimension={format.humanise(dimension)}
      />
    </PanelBody>
  </Panel>
);

export const TrendsTab: React.FC<TabContext> = ({
  filter,
  filterMeta,
  meta,
  data,
  dispatch,
  isMobile,
  onExportPanelPng,
  onExportPanelCsv,
}) => {
  const results = useResults(data);
  const zoom = useChartZoom({ filter, dispatch });

  const measure = filterMeta?.measures.find(m => m.key === filter.measure);
  const measureLabel = measure?.label || filter.measure;
  const dimensions = measure?.dimensions || [];

  /* Until meta has landed there is no measure to check a dimension against, so a
     panel is assumed POSSIBLE and renders its skeleton. Assuming the opposite
     would flash "not available for this measure" on every first paint. */
  const ready = Boolean(filterMeta);
  const has = (dimension: string): boolean => !ready || dimensions.includes(dimension);

  const showPortfolios = (filterMeta?.portfolios.length || 0) > 1;
  const rhythmOk = !ready || (filterMeta?.rhythmMeasures || []).includes(filter.measure);

  const batchState = {
    loading: data.batch.loading,
    stale: data.batch.stale,
    error: data.batch.error,
    onRetry: data.batch.retry,
  };

  const dayDrill = useChartDrill({
    panelId: 'pulse',
    dimension: filter.granularity === 'week' ? 'week' : 'day',
    filter,
    dispatch,
  });
  const buildingDrill = useChartDrill({
    panelId: 'league',
    dimension: 'building',
    filter,
    dispatch,
  });
  const portfolioDrill = useChartDrill({
    panelId: 'portfolio',
    dimension: 'portfolio',
    filter,
    dispatch,
  });
  const kindDrill = useChartDrill({
    panelId: 'composition',
    dimension: 'kind',
    filter,
    dispatch,
  });
  const severityDrill = useChartDrill({
    panelId: 'severity',
    dimension: 'severity',
    filter,
    dispatch,
  });
  const hourDrill = useChartDrill({
    panelId: 'hour',
    dimension: 'hour_of_day',
    filter,
    dispatch,
  });
  const staffDrill = useChartDrill({
    panelId: 'staff',
    dimension: 'staff',
    filter,
    dispatch,
  });

  const onRhythmCell = useCallback(
    (weekday: number, hour: number) => {
      const hh = String(hour).padStart(2, '0');
      dispatch({
        type: 'drill',
        selection: {
          source: 'rhythm',
          dimension: 'hour_of_day',
          key: hh,
          label: `${data.rhythm.data?.weekdays?.[weekday - 1] || ''} ${hh}:00`,
        },
        patch: { timeFrom: `${hh}:00`, timeTo: `${hh}:59` },
      });
    },
    [dispatch, data.rhythm.data]
  );

  const window = data.batch.data?.window || data.overview.data?.window;

  return (
    <PanelGrid data-testid="panel-grid">
      <PulsePanel
        state={batchState}
        entries={results[PANEL.pulseEntries]}
        incidents={results[PANEL.pulseIncidents]}
        comparing={filter.compare === 'previous_period'}
        endsAt={partialDayEndsAt(window?.to, window?.tz || 'UTC')}
        seriesOff={filter.seriesOff}
        onToggleSeries={key =>
          dispatch({ type: 'toggleMulti', field: 'seriesOff', value: `pulse:${key}` })
        }
        drill={dayDrill}
        zoom={zoom}
        showBrush={!isMobile}
        onExportPng={svg => onExportPanelPng('pulse', svg)}
        onExportCsv={() => onExportPanelCsv(PANEL.pulseEntries)}
      />

      {/* VOLUME AND THE INCIDENT SHARE — the one dual-axis chart on this screen,
          and the only place two series here have genuinely different units. Both
          series come from the same batch, so it costs no request. */}
      <RatePanel
        state={batchState}
        entries={results[PANEL.pulseEntries]}
        incidents={results[PANEL.pulseIncidents]}
        granularity={filter.granularity}
        seriesOff={filter.seriesOff}
        onToggleSeries={key =>
          dispatch({ type: 'toggleMulti', field: 'seriesOff', value: `rate:${key}` })
        }
        drill={dayDrill}
        onExportPng={svg => onExportPanelPng('rate', svg)}
        onExportCsv={() => onExportPanelCsv(PANEL.pulseEntries)}
      />

      {has('building') ? (
        <BarPanel
          id="league"
          title="Building league"
          wide
          state={batchState}
          result={results[PANEL.league]}
          caveat={`${measureLabel} per building, ranked. A building that recorded nothing still has a row — that is the most useful row on this panel. Choose one to scope the whole page to it.`}
          emptyTitle="No buildings in scope"
          emptyText="Nothing you can see belongs to this selection, so there is nothing to rank."
          onSelect={item => buildingDrill.onValue(item.key, item.label)}
          selectVerb="Scope the page to"
          onExportCsv={() => onExportPanelCsv(PANEL.league)}
        />
      ) : null}

      {/* WHICH BUILDING RECORDS AN UNUSUAL NUMBER OF INCIDENTS FOR ITS SIZE —
          the question a ranked list cannot answer, because the answer sits in
          the middle of the ranking where nobody looks. */}
      {has('building') && filter.measure !== 'incidents' ? (
        <ScatterPanel
          state={batchState}
          league={results[PANEL.league]}
          incidents={results[PANEL.leagueIncidents]}
          drill={buildingDrill}
          onExportPng={svg => onExportPanelPng('outliers', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.league)}
        />
      ) : null}

      {/* NESTING, ONLY WHERE THERE IS NESTING. With one portfolio the hierarchy
          has one level and the ranked list above is strictly better, so the
          panel is not rendered rather than rendered with an apology. */}
      {has('building') && (filterMeta?.portfolios.length || 0) > 1 ? (
        <TreemapPanel
          state={batchState}
          league={results[PANEL.league]}
          meta={filterMeta}
          drill={buildingDrill}
          onExportPng={svg => onExportPanelPng('treemap', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.league)}
        />
      ) : null}

      {/* THE ONLY PANEL ON THIS SCREEN THAT COSTS A REQUEST PER BUILDING.
          Off until asked for, capped, and every request queued at the lowest
          priority behind the panels the reader is actually looking at. */}
      {has('building') ? (
        <SmallMultiplesPanel
          filter={filter}
          meta={filterMeta}
          league={results[PANEL.league]}
          state={batchState}
          onSelectBuilding={(id, name) => buildingDrill.onValue(String(id), name)}
        />
      ) : null}

      {has('kind') ? (
        <CompositionPanel
          state={batchState}
          result={results[PANEL.composition]}
          isMobile={isMobile}
          drill={kindDrill}
          onExportPng={svg => onExportPanelPng('composition', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.composition)}
        />
      ) : (
        <Unavailable
          title="Composition"
          measureLabel={measureLabel}
          dimension="kind"
        />
      )}

      {has('severity') ? (
        <SeverityPanel
          state={batchState}
          result={results[PANEL.severity]}
          drill={severityDrill}
          onExportPng={svg => onExportPanelPng('severity', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.severity)}
        />
      ) : (
        <Unavailable
          title="Severity"
          measureLabel={measureLabel}
          dimension="severity"
        />
      )}

      {showPortfolios && has('portfolio') ? (
        <BarPanel
          id="portfolio"
          title="Portfolio roll-up"
          state={batchState}
          result={results[PANEL.portfolio]}
          caveat={`${measureLabel} per portfolio, counted through the buildings in it.`}
          emptyTitle="No portfolios in scope"
          emptyText="None of the buildings you can see belong to a portfolio yet."
          onSelect={item => portfolioDrill.onValue(item.key, item.label)}
          selectVerb="Scope the page to"
          onExportCsv={() => onExportPanelCsv(PANEL.portfolio)}
        />
      ) : null}

      {has('hour_of_day') ? (
        <HourPanel
          state={batchState}
          result={results[PANEL.hour]}
          drill={hourDrill}
          onExportPng={svg => onExportPanelPng('hour', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.hour)}
        />
      ) : (
        <Unavailable
          title="Hour of day"
          measureLabel={measureLabel}
          dimension="hour of the day"
          wide
        />
      )}

      {has('staff') ? (
        <BarPanel
          id="staff"
          title="Who recorded it"
          wide
          state={batchState}
          result={results[PANEL.staff]}
          /* PERMANENT VISIBLE COPY, NOT A HOVER. An entry count per person is the
             most convincing thing on this screen that could be mistaken for a
             shift-hours report, and Lobbero has no such report. */
          caveat="This is who WROTE EACH ITEM DOWN — not who was on duty, and not for how long. Somebody who worked a quiet shift recorded less, not less time."
          emptyTitle="Nothing was recorded by anybody"
          emptyText="No records in this period carry an author, so there is nobody to list."
          onSelect={item => staffDrill.onValue(item.key, item.label)}
          selectVerb="Show only what was recorded by"
          onExportCsv={() => onExportPanelCsv(PANEL.staff)}
        />
      ) : (
        <Unavailable
          title="Who recorded it"
          measureLabel={measureLabel}
          dimension="person"
          wide
        />
      )}

      {/* ── the four questions a count cannot answer ── */}
      <DwellPanel
        state={{
          loading: data.latency.loading,
          stale: data.latency.stale,
          error: data.latency.error,
          onRetry: data.latency.retry,
        }}
        data={data.latency.data}
        subjects={meta?.latency?.subjects || []}
        subject={filter.subject}
        onSubject={next => dispatch({ type: 'patch', patch: { subject: next } })}
        onExportCsv={() => onExportPanelCsv('dwell')}
      />

      <QualityPanel
        state={{
          loading: data.quality.loading,
          stale: data.quality.stale,
          error: data.quality.error,
          onRetry: data.quality.retry,
        }}
        data={data.quality.data}
        drill={filter.qualityDrill}
        onDrill={key => dispatch({ type: 'patch', patch: { qualityDrill: key } })}
        drillData={data.qualityDrill.data}
        drillLoading={data.qualityDrill.loading}
        drillError={data.qualityDrill.error}
        onExportCsv={() => onExportPanelCsv('quality')}
      />

      <TurnoverPanel
        state={{
          loading: data.turnover.loading,
          stale: data.turnover.stale,
          error: data.turnover.error,
          onRetry: data.turnover.retry,
        }}
        data={data.turnover.data}
        onExportCsv={() => onExportPanelCsv('turnover')}
      />

      {rhythmOk ? (
        <RhythmPanel
          state={{
            loading: data.rhythm.loading,
            stale: data.rhythm.stale,
            error: data.rhythm.error,
            onRetry: data.rhythm.retry,
          }}
          data={data.rhythm.data}
          onCell={onRhythmCell}
          onExportCsv={() => onExportPanelCsv('rhythm')}
        />
      ) : (
        <Unavailable
          title="Rhythm — when things get recorded"
          measureLabel={measureLabel}
          dimension="hour of the day"
          wide
        />
      )}

      <AllFiguresPanel
        state={{
          loading: data.overview.loading,
          stale: data.overview.stale,
          error: data.overview.error,
          onRetry: data.overview.retry,
        }}
        overview={data.overview.data}
        onExportCsv={() => onExportPanelCsv('all-figures')}
      />
    </PanelGrid>
  );
};
